import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";

interface SocialAccountBasic {
  id: string;
  platform: string;
  username: string | null;
  isActive: boolean | null;
}

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = "Luxury Escorts Manchester";
const DEFAULT_DESCRIPTION =
  "Premium Manchester escorts available 24/7. Incall & outcall across Greater Manchester. Browse verified profiles & reviews.";
const BASE_URL = "https://luxuryescortsmanchester.co.uk";

export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = `${BASE_URL}/icons/icon-512.png`,
  ogType = "website",
  noindex = false,
  jsonLd,
}: SEOHeadProps) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `Manchester Escorts | ${SITE_NAME} | Book 24/7`;
  const canonicalUrl = canonical
    ? `${BASE_URL}${canonical}`
    : undefined;

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
    : [];

  // Fetch twitter account for twitter:site meta tag
  const { data: socialAccounts = [] } = useQuery<SocialAccountBasic[]>({
    queryKey: ["/api/social/accounts"],
    staleTime: 10 * 60 * 1000,
  });
  const twitterAccount = socialAccounts.find(a => a.platform === "twitter" && a.isActive);
  const twitterSite = twitterAccount?.username
    ? (twitterAccount.username.startsWith("@") ? twitterAccount.username : `@${twitterAccount.username}`)
    : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={ogTitle || fullTitle} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      <meta name="twitter:title" content={ogTitle || fullTitle} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLdArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

// Pre-built schema generators
export const schemas = {
  localBusiness: () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Luxury Escorts Manchester",
    description: DEFAULT_DESCRIPTION,
    url: BASE_URL,
    telephone: "+447000000000",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Manchester",
      addressRegion: "Greater Manchester",
      addressCountry: "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 53.4808,
      longitude: -2.2426,
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 53.4808,
        longitude: -2.2426,
      },
      geoRadius: "50000",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday", "Tuesday", "Wednesday", "Thursday",
        "Friday", "Saturday", "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
    priceRange: "£££",
    image: `${BASE_URL}/icons/icon-512.png`,
  }),

  organization: () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Luxury Escorts Manchester",
    url: BASE_URL,
    logo: `${BASE_URL}/icons/icon-512.png`,
    description: "Premium escort agency in Manchester offering verified companions for incall and outcall bookings across Greater Manchester.",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      availableLanguage: "English",
      areaServed: "GB",
    },
  }),

  breadcrumbList: (items: { name: string; url: string }[]) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  }),

  faqPage: (faqs: { question: string; answer: string }[]) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  }),
};
